import React, { useState, useEffect, useContext, useCallback } from "react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { ICustomerSystemUser, IEnumerationEntry } from "src/stores/CustomerDetailPageStore";
import { useForm } from "react-hook-form";
import { useRegisterRef } from "src/utils/Form";
import { MessageType } from "src/components/notifications/Notifier";
import { CopyToClipboardButton } from "src/components/copy-to-clipboard/CopyToClipboardButton";
import styles from "./CustomerAdminuserInfo.module.css";

/** Ordnet jedem Schlüssel seine Beschriftung zu; der Endpoint liefert nur Einträge mit Schlüssel. */
const getEnumerationMap = (enumerations: IEnumerationEntry[]) => {
    return new Map(enumerations.map((enumeration) => [enumeration.key, { label: enumeration.label }]));
};

interface CustomerAdminuserInfoProps {
    adminuser: ICustomerSystemUser | undefined;
    userroleEnumerations: IEnumerationEntry[];
    customerId?: string;
}

interface AdminRolesFields {
    adminroles: number[];
}
const CustomerAdminuserInfo = (props: CustomerAdminuserInfoProps) => {
    const { adminuser, userroleEnumerations, customerId } = props;
    const { register, handleSubmit, reset } = useForm<AdminRolesFields>();
    const registerRef = useRegisterRef(register);

    const { authStore, uiStore } = useContext(RootStoreContext);
    const [isChangingAdminState, setIsChangingAdminState] = useState(false);
    const [password, setPassword] = useState<string | null>();
    const [warningMessage, setWarningMessage] = useState<string>("");

    const [umsUserroles, setUmsUserroles] = useState<Map<number, { label: string | null }>>();
    const [adminUserroles, setAdminUserroles] = useState<Array<Number>>();
    const [isAdminActivated, setAdminActivated] = useState<boolean>(adminuser ? adminuser.registered : false);

    useEffect(() => {
        if (userroleEnumerations) {
            setUmsUserroles(getEnumerationMap(userroleEnumerations));
        }
    }, [userroleEnumerations]);

    useEffect(() => {
        if (adminuser?.roles) {
            setAdminUserroles(adminuser.roles);
        }
    }, [adminuser?.roles]);

    const activateAdmin = useCallback(
        async (formData: AdminRolesFields) => {
            setIsChangingAdminState(true);

            const rolesToActivate: number[] = [];

            for (const [key, value] of Object.entries(formData.adminroles)) {
                const roleNumber = key;
                const isRoleSelected = value;

                if (isRoleSelected) {
                    rolesToActivate.push(Number(roleNumber));
                }
            }

            if (rolesToActivate.length === 0) {
                setWarningMessage("Du musst mindestens eine Rolle auswählen");
                setIsChangingAdminState(false);
                return;
            } else {
                setWarningMessage("");
            }

            if (authStore.user) {
                const accessToken = authStore.token;
                const tokenType = "Bearer";
                const role = authStore.user?.role;

                const requestBody = {
                    customerid: customerId,
                    roles: rolesToActivate
                };

                try {
                    const fetchResult = await fetch(`${NetworkConfig.activateAdminUrl}`, {
                        method: "POST",
                        body: JSON.stringify(requestBody),
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `${tokenType} ${accessToken}`,
                            "x-hasura-role": getRoleKey(role)
                        }
                    });

                    if (fetchResult.status === 200) {
                        try {
                            const returnedData = await fetchResult.json();
                            const { password } = returnedData.data;
                            setPassword(password);
                        } catch (jsonError) {
                            console.error("jsonError: ", jsonError);
                        } finally {
                            setAdminActivated(true);
                            uiStore.printStatusMessage(
                                "Der Adminbenutzer wurde erfolgreich aktiviert",
                                MessageType.INFO
                            );
                        }
                    } else {
                        const returnValue = await fetchResult.json();
                        uiStore.printStatusMessage(
                            `Es ist ein Fehler aufgetreten: ${returnValue.message}`,
                            MessageType.ERROR
                        );
                    }
                } catch (fetchError) {
                    uiStore.printStatusMessage(`Es ist ein Fehler aufgetreten: ${fetchError.error}`, MessageType.ERROR);
                }
            }
            setIsChangingAdminState(false);
        },
        [authStore.user, authStore.token, uiStore, customerId]
    );

    const deactivateAdmin = useCallback(async () => {
        setIsChangingAdminState(true);

        if (authStore.user) {
            const accessToken = authStore.token;
            const tokenType = "Bearer";
            const role = authStore.user?.role;

            const requestBody = {
                customerid: customerId
            };

            try {
                const fetchResult = await fetch(`${NetworkConfig.deactivateAdminUrl}`, {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    }
                });

                if (fetchResult.status === 200) {
                    uiStore.printStatusMessage("Der Adminbenutzer wurde erfolgreich deaktiviert", MessageType.INFO);
                    setAdminActivated(false);
                    setPassword("");
                    reset({
                        adminroles: []
                    });
                } else {
                    const returnValue = await fetchResult.json();
                    uiStore.printStatusMessage(
                        `Es ist ein Fehler aufgetreten: ${returnValue.message}`,
                        MessageType.ERROR
                    );
                }
            } catch (fetchError) {
                uiStore.printStatusMessage(`Es ist ein Fehler aufgetreten: ${fetchError.error}`, MessageType.ERROR);
            }
        }
        setWarningMessage("");
        setIsChangingAdminState(false);
    }, [authStore.user, authStore.token, uiStore, reset, customerId]);

    const renderRolesCheckboxes = useCallback(() => {
        const userroleComponents: any = [];

        if (umsUserroles) {
            umsUserroles.forEach(({ label }, key) => {
                const roleName = label;
                const roleNumber = key;

                let hasRole = false;

                if (adminUserroles && roleNumber) {
                    hasRole = adminUserroles.includes(roleNumber) ? true : false;
                }

                userroleComponents.push(
                    <UI.Checkbox
                        key={roleNumber}
                        ref={registerRef}
                        name="adminroles"
                        label={roleName}
                        checkboxValue={roleNumber ?? 0}
                        defaultChecked={hasRole}
                        disabled={isAdminActivated}
                    />
                );
            });
        }

        return <>{userroleComponents}</>;
    }, [adminUserroles, register, umsUserroles, isAdminActivated]);

    return (
        <div>
            <div className={styles.infoBlock}>
                <p className={styles.infoHeading}>Benutzername Administrator</p>
                <div className={styles.valueWithCopyButton}>
                    <p className={styles.infoText}>{adminuser?.name}</p>
                    {adminuser?.name ? <CopyToClipboardButton value={adminuser.name} /> : undefined}
                </div>
            </div>
            <div className={styles.infoBlock}>
                <div>
                    <p className={`mb-1 ${styles.infoHeading}`}>Zugewiesene Rollen</p>
                    {renderRolesCheckboxes()}
                    <div className="mt-2">
                        {isAdminActivated ? (
                            <UI.Button
                                label="Admin deaktivieren"
                                onClick={deactivateAdmin}
                                disabled={isChangingAdminState}
                                className="customer-button w-100"
                            />
                        ) : (
                            <UI.Button
                                label="Admin aktivieren"
                                onClick={handleSubmit(activateAdmin)}
                                disabled={isChangingAdminState}
                                className="customer-button w-100"
                            />
                        )}
                    </div>
                </div>
                <div>
                    {isChangingAdminState ? (
                        <UI.RotatingSpinner noLogo={true} size={30} className={"align-items-center mt-3"} />
                    ) : password ? (
                        <div className="mt-2">
                            <p className={styles.infoHeading}>Neues Passwort:</p>
                            <div className={styles.valueWithCopyButton}>
                                <p className={styles.infoText}>{password}</p>
                                <CopyToClipboardButton value={password} />
                            </div>
                        </div>
                    ) : undefined}
                </div>
                <p className={`${styles.warningText} mt-2`}>{warningMessage}</p>
            </div>
        </div>
    );
};

export default CustomerAdminuserInfo;
